<template>
  <div class="wrapper">
    <div class="header">
      <div class="header__title">
        Редактирование источника
      </div>
      <div
        class="header__close-button"
        @click="emit('close-feed-modal')"
      >
        <CrossIcon />
      </div>
    </div>

    <div class="body scroll">
      <div class="list">
        <div class="list__item">
          <div class="list__item-subtitle required-label">
            Имя
          </div>
          <input
            v-model="feedParams.name"
            :class="{'input-error': errors.name}"
            @blur="validateField('name')"
          >
        </div>
        <div class="list__item">
          <InputUrl
            :url-list="urlList"
            @add-url="url => urlList.push(url)"
            @delete-url="index => urlList.splice(index, 1)"
          />
          <span class="list__item--error">{{ errors.start_url }}</span>
          <span class="list__item--error">{{ errors.feedUrl }}</span>
        </div>
        <div class="list__item">
          <UpdateFrequency v-model="feedParams.cron" />
        </div>
        <div class="list__item">
          <GlobusSelector
            v-model="feedParams.type "
            title="Тип"
            :items="tableStore.filterOptions.typeOptions"
            :show="areTypesVisibles"
            :required="true"
            @update:show="val => areTypesVisibles = val"
          />
        </div>
        <div class="list__item">
          <GlobusSelector
            v-model="feedParams.language "
            title="Язык"
            :items="tableStore.filterOptions.languageOptions"
            :show="areLanguagesVisibles"
            :required="true"
            @update:show="val => areLanguagesVisibles = val"
          />
        </div>
        <div class="list__item">
          <TagSelector
            v-if="!isTagsLoading"
            :tag-list="tagList"
            @update-tag-ids="args => updateTagIds(args)"
          />
        </div>
      </div>
    </div>

    <div class="footer">
      <ButtonAccentPrimary
        size="large"
        class="navbar__action-button"
        :disabled="feedParams.formSubmited"
        @click="handleCreateFeed"
      >
        Сохранить
      </ButtonAccentPrimary>
      <ButtonNeutralSecondary
        @click="emit('close-feed-modal')"
      >
        Отмена
      </ButtonNeutralSecondary>
    </div>
  </div>
</template>

<script setup>
import {defineProps, reactive, ref, defineEmits, onMounted} from "vue";
import { useTableStore } from "@/stores/table.js";
import UpdateFrequency from "@/components/table/CreateFeed/UpdateFrequency.vue";
import InputUrl from "@/components/table/CreateFeed/InputUrl.vue";
import CrossIcon from "@/components/icons/CrossIcon.vue";
import TagSelector from "@/components/table/CreateFeed/TagSelector.vue";
import {useSources} from "@/stores/sources-store";
import GlobusSelector from "@/components/UI/GlobusSelector.vue";

const emit = defineEmits(["update:isActive", "added", "close-feed-modal"]);
const tableStore = useTableStore();

const props = defineProps({
  source: {
    type: Object,
    default: null
  }
});

const feedParams = reactive({
  type: "rss",
  name: "",
  description: "",
  language: "",
  start_url: "",
  feed_url: "",
  is_archived: false,
  is_enabled: true,
  cron: null,
  tag_ids: [],
  tags: [],
  settings: {
    username: "",
    password: "",
    user_agent: "",
    crawler: true,
    ignore_http_cache: true,
    fetch_via_proxy: true
  }
});

const areTypesVisibles = ref(false);
const areLanguagesVisibles = ref(false);

const sourcesStore = useSources();

const urlList = ref([{start_url: "", feed_url: ""}]);

if (props.source) {
  Object.assign(
      feedParams,
      // JSON-клон нужен, чтобы не тащить за собой реактивность родителя
      JSON.parse(JSON.stringify(props.source))
  );
}

onMounted(async () => {
  urlList.value[0].start_url = props.source.start_url;
  urlList.value[0].feed_url = props.source.feed_url;
  await getTags();
});

const isTagsLoading = ref(false);
const tagList = ref([]);

const getTags = async () => {
  isTagsLoading.value = true;
  
  // Группируем теги по категориям
  const tagsByCategory = {};
  for (const tag of feedParams.tags) {
    if (!tagsByCategory[tag.category]) {
      tagsByCategory[tag.category] = [];
    }
    tagsByCategory[tag.category].push({ name: tag.name, id: tag.id });
  }

  // Создаем структуру для каждой категории с группированными тегами
  tagList.value = await Promise.all(
      Object.entries(tagsByCategory).map(async ([category, tags]) => (
          {
            category_active: category,
            tag_list: await sourcesStore.getTagByCategory(category),
            tag_active: tags
          }))
  );
  
  isTagsLoading.value = false;
};

const updateTagIds = (tags) => {
  // tags - это массив ID (от TagSelector через emit "update-tag-ids")
  feedParams.tag_ids = Array.isArray(tags) ? tags : [];
};

const errors = reactive({
  name: null,
  start_url: null,
  feedUrl: null,
  type: null,
  // другие поля...
});

// Валидация конкретного поля
const validateField = (field) => {
  switch (field) {
    case "name":
      if (feedParams.name.length === 0) {
        errors.name = "Название обязательно.";
      } else {
        errors.name = null;
      }
      break;
    case "start_url":
      if (!isValidUrl(urlList.value[0].start_url)) {
        errors.start_url = "Некорректный формат Url.";
      }
      break;
    case "feed_url":
      if (!isValidUrl(urlList.value[0].feed_url) && urlList.value[0].feed_url?.length > 0) {
        errors.feedUrl = "Некорректный формат Feed URL.";
      }
      break;
      // Валидация других полей...
  }
};

// Метод для проверки URL
const isValidUrl = (url) => {
  const pattern = new RegExp(
      "^(https?:\\/\\/)" + // Проверка на обязательный протокол (http/https)
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|" + // Доменное имя
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // или IP (v4) адрес
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+@]*)*" + // Путь
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // Строка запроса
      "(\\#[-a-z\\d_]*)?$",
      "i"
  );
  return pattern.test(url);
};

// Метод для проверки всех полей
const validateFields = () => {
  Object.keys(errors).forEach(key => {
    errors[key] = null;
  });

  validateField("start_url");
  validateField("feed_url");
  validateField("name");
  // Добавляем другие поля в валидацию...

  return !errors.start_url && !errors.name && !errors.feedUrl;
};

// Метод для создания фида
const handleCreateFeed = () => {
  if (validateFields()) {
    createFeed();
  }
};

const createFeed = async () => {
  try {
    feedParams.formSubmited = true;

    const payload = {
      name: feedParams.name,
      feed_url: urlList.value[0].feed_url?.length > 0 ? urlList.value[0].feed_url : null,
      start_url: urlList.value[0].start_url,
      type: feedParams.type,
      tag_ids: feedParams.tag_ids,
      cron: feedParams.cron,
      language: feedParams.language,
    };

    await sourcesStore.updateSource(feedParams.id, payload);

    emit("added");
    emit("close-feed-modal");
  } catch (err) {
    console.error(err);
  } finally {
    feedParams.formSubmited = false;
  }
};
</script>

<style lang="scss" scoped>
@use '@/assets/styles/vars.scss';

.title {
  margin-bottom: 30px;
  font-weight: 500;
  font-size: 16px;
  line-height: 150%;
  color: #212327;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  &__item {

    &--error {
      padding: 4px;
      color: red;
    }

    & input {
      border: 1px solid var(--outline-input);
      border-radius: 8px;
      padding: 8px 12px;
      width: 100%;
      height: 40px;
      font-size: 14px;
      &::placeholder {
        color: var(--outline-grey3);
      }
    }
    & select {
      width: 100%;
      height: 40px;
      padding: 10px 12px;
      background: white;
      border: 1px solid var(--outline-input);
      border-radius: 8px;
      font-size: 14px;
    }
  }

  &__item-subtitle {
    font-weight: 500;
    font-size: 14px;
    line-height: 140%;
    color: var(--outline-grey3);
    margin-bottom: 10px;
  }
}

.category-tags {
  margin: 24px 0;
  padding: 24px;
  border-radius: 8px;
  background: #F7F8FD;
  &__title {
    flex: 0 0 100%;
    font-size: 16px;
    color: var(--outline-grey3);
  }
  &.list {
    display: flex;
    flex-wrap: wrap;
    gap: 0 24px;

    .list__item {
      position: relative;
      width: 100%;
      margin-top: 24px;

      &-subtitle {
        position: relative;
      }
      &--remove {
        position: absolute;
        right: 0;
        top: 6px;
        cursor: pointer;
      }
    }
  }
}

.tags {
  display: flex;
  gap: 5px;
  border: 1px solid var(--outline-input);
  border-radius: 8px;
  padding: 6px 12px 6px 4px;
  min-height: 44px;

}

.tag {
  font-family: var(--font-family);
  font-weight: 400;
  font-size: 14px;
  line-height: 143%;
  color: var(--outline-grey3);
}
.error-message {
  color: red;
  font-size: 12px;
  margin-top: 5px;
}

.wrapper {
  overflow-y: hidden;
  position: fixed;
  top: 0;
  right: 0;
  z-index: 999;
  display: flex;
  flex-direction: column;
  width: 756px;
  height: 100%;
  padding: 20px 10px 20px 20px;
  background: #FFF;
  box-shadow: 0 4px 20px 0 #00000026;
  gap: 30px;

  .footer {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 24px;
    margin-top: auto;
  }

  .body {
    height: 100%;
    padding-right: 10px;
    padding-bottom: 100px;
  }

  .header {
    display: flex;
    justify-content: space-between;

    &__close-button {
      cursor: pointer;
      user-select: none;
      color: #CACACA;
    }

    &__title {
      font-size: 16px;
      font-weight: 500;
      color: #212327;
    }

    &__button-close {
      cursor: pointer;
      user-select: none;
      color: #A7A7A7;
    }
  }

}
</style>
